// src/features/chat/components/his/ApprovalCard.jsx
/*
 * An approval card is a button for one concrete reviewed operation.
 * Pending: a quiet card with outcome-labeled buttons, waiting in place,
 * ignorable without cost. Resolved: a one-line whisper. Conflict / failure /
 * expiry: a plain-language terminal trace with a recovery seed.
 *
 * All phases live in one component so resolution collapses in place. Every
 * string renders from the template library — no model-authored card text.
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Check,
  ChevronDown,
  ChevronUp,
  Eye,
  FileText,
  Trash2,
  Users,
  X,
} from 'lucide-react';
import styles from '../../styles/HisArtifact.module.css';
import Collapse from './Collapse';
import ArtifactMetaLine from './ArtifactMetaLine';
import SemanticDiff from './SemanticDiff';
import { approvalDisplayState } from '../../models/artifactLifecycleModel';
import {
  approvalButtons,
  approvalDetails,
  approvalFloor,
  approvalRecordLinks,
  approvalSummary,
  approvalTier,
  approvalTitle,
  boundaryChip,
} from '../../templates/approvalCardTemplates';
import {
  approvalWhisper,
  terminalTrace,
} from '../../templates/terminalTraceTemplates';

function operationIcon(approval) {
  switch (approval?.operationKind) {
    case 'record.access.grant':
    case 'record.access.role_change':
    case 'record.access.revoke':
      return <Users size={15} aria-hidden="true" />;
    case 'record.visibility.set':
      return <Eye size={15} aria-hidden="true" />;
    case 'record.delete':
      return <Trash2 size={15} aria-hidden="true" />;
    default:
      return <FileText size={15} aria-hidden="true" />;
  }
}

function FactText({ fact }) {
  if (fact.strong && fact.text.includes(fact.strong)) {
    const index = fact.text.indexOf(fact.strong);
    return (
      <span>
        {fact.text.slice(0, index)}
        <b>{fact.strong}</b>
        {fact.text.slice(index + fact.strong.length)}
      </span>
    );
  }
  return <span>{fact.text}</span>;
}

export function ApprovalCard({
  approval,
  born = false,
  confirmBusy = false,
  declineBusy = false,
  disabled = false,
  notice = null,
  showMeta = true,
  onDecision,
  onSeedComposer,
  botName,
  timeMs = 0,
}) {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const state = approvalDisplayState(approval);
  const pending = state === 'pending';
  const tier = approvalTier(approval);
  const buttons = approvalButtons(approval);
  const floor = approvalFloor(approval);
  const details = approvalDetails(approval);
  const recordLinks = approvalRecordLinks(approval);
  // The card must be decidable wherever it lands: looking at the Record
  // directly stays one tap from the card face, not folded away.
  const primaryRecordLink = recordLinks[0] || null;
  const linkedRecordLinks = recordLinks.slice(1);
  const busy = confirmBusy || declineBusy;
  const hasDetails = Boolean(details) || linkedRecordLinks.length > 0;

  const renderSettled = () => {
    if (state === 'trace') {
      const trace = terminalTrace(approval);
      return (
        <div className={styles.whisper} data-quiet="true">
          <span className={styles.whisperGlyphMuted} aria-hidden="true">·</span>
          <span>
            {trace.text}
            {trace.seed && typeof onSeedComposer === 'function' && (
              <>
                <span className={styles.whisperSep}> · </span>
                <button
                  type="button"
                  className={styles.whisperLink}
                  onClick={() => onSeedComposer(trace.seed.text)}
                >
                  {trace.seed.label}
                </button>
              </>
            )}
          </span>
        </div>
      );
    }

    const whisper = approvalWhisper(approval);
    const confirmed = state === 'resolved_confirmed';
    const openLink = confirmed && recordLinks.length > 0 ? recordLinks[0] : null;
    return (
      <div className={styles.whisper} data-quiet={confirmed ? 'false' : 'true'}>
        {confirmed ? (
          <span className={styles.whisperGlyphOk} aria-hidden="true">
            <Check size={13} strokeWidth={2.4} />
          </span>
        ) : (
          <span className={styles.whisperGlyphMuted} aria-hidden="true">
            <X size={13} strokeWidth={2.2} />
          </span>
        )}
        <span>
          {whisper.text}
          {openLink && (
            <>
              <span className={styles.whisperSep}> · </span>
              <Link to={`/record/${openLink.id}`} className={styles.whisperLink}>
                Open
              </Link>
            </>
          )}
          {whisper.warning && (
            <span className={styles.traceWarning}>{whisper.warning}</span>
          )}
        </span>
      </div>
    );
  };

  return (
    <div
      className={[styles.card, born ? styles.rise : ''].filter(Boolean).join(' ')}
      data-kind="approval"
      data-tier={pending ? tier : undefined}
      data-settled={pending ? 'false' : 'true'}
    >
      <Collapse open={pending}>
        <div>
          {showMeta && <ArtifactMetaLine botName={botName} timeMs={timeMs} />}
          <div className={styles.header}>
            <span className={styles.headerIcon}>{operationIcon(approval)}</span>
            <span className={styles.title}>{approvalTitle(approval)}</span>
            {boundaryChip(approval) && (
              <span className={styles.tierChip}>
                <Users size={11} strokeWidth={2.2} aria-hidden="true" />
                {boundaryChip(approval)}
              </span>
            )}
          </div>
          <div className={styles.body}>
            {approvalSummary(approval) && (
              <p className={styles.summary}>{approvalSummary(approval)}</p>
            )}

            {floor.length > 0 && (
              <div className={styles.floor}>
                {floor.map((fact) => (
                  <span key={fact.id} className={styles.fact}>
                    <FactText fact={fact} />
                  </span>
                ))}
              </div>
            )}

            {primaryRecordLink && (
              <div className={styles.linkRow}>
                <Link
                  to={`/record/${primaryRecordLink.id}`}
                  className={styles.recordLink}
                >
                  <FileText size={13} aria-hidden="true" />
                  <span>{primaryRecordLink.label}</span>
                </Link>
              </div>
            )}

            {hasDetails && (
              <>
                <button
                  type="button"
                  className={styles.detailsButton}
                  onClick={() => setDetailsOpen((open) => !open)}
                  aria-expanded={detailsOpen}
                >
                  Details
                  {detailsOpen ? (
                    <ChevronUp size={12} aria-hidden="true" />
                  ) : (
                    <ChevronDown size={12} aria-hidden="true" />
                  )}
                </button>
                <Collapse open={detailsOpen}>
                  <div className={styles.details}>
                    {linkedRecordLinks.length > 0 && (
                      <div className={styles.linkRow}>
                        {linkedRecordLinks.map((record) => (
                          <Link
                            key={`${record.label}:${record.id}`}
                            to={`/record/${record.id}`}
                            className={styles.recordLink}
                          >
                            <FileText size={13} aria-hidden="true" />
                            <span>{record.label}</span>
                          </Link>
                        ))}
                      </div>
                    )}
                    {details?.kind === 'diff' && <SemanticDiff rows={details.rows} />}
                    {details?.kind === 'bullets' && (
                      <div className={styles.bullets}>
                        {details.bullets.map((bullet) => (
                          <span key={bullet.id} className={styles.fact}>
                            <span>{bullet.text}</span>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </Collapse>
              </>
            )}

            {notice && <p className={styles.inlineNotice}>{notice}</p>}

            <div className={styles.actions}>
              <button
                type="button"
                className={styles.button}
                data-kind="primary"
                onClick={() => onDecision?.(approval.id, 'confirm')}
                disabled={disabled || busy || !onDecision}
              >
                {confirmBusy && <span className={styles.spinner} aria-hidden="true" />}
                {buttons.confirm}
              </button>
              <button
                type="button"
                className={styles.button}
                data-kind="ghost"
                onClick={() => onDecision?.(approval.id, 'decline')}
                disabled={disabled || busy || !onDecision}
              >
                {declineBusy && <span className={styles.spinner} aria-hidden="true" />}
                {buttons.decline}
              </button>
            </div>
          </div>
        </div>
      </Collapse>

      <Collapse open={!pending}>{renderSettled()}</Collapse>
    </div>
  );
}

export default ApprovalCard;
